import type { CSSProperties, HTMLAttributes, ReactNode, Ref } from 'react'

const cx = (...classes: (string | undefined | false | null)[]) => classes.filter(Boolean).join(' ')

interface HeaderWrapperProps extends HTMLAttributes<HTMLElement> {
  ref?: Ref<HTMLElement>
  isMainPages?: boolean
  isLoginPage?: boolean
  isHome?: boolean
}

export const HeaderWrapper = ({ ref, isMainPages, isLoginPage, isHome, className, style, children, ...rest }: HeaderWrapperProps) => (
  <header
    ref={ref}
    className={cx(
      'text-white flex items-center h-[70px] pt-2.5 fixed top-0 z-[1000]',
      isLoginPage ? 'w-[40%] cursor-default' : 'w-[-webkit-fill-available] cursor-pointer',
      className
    )}
    style={{
      padding: isMainPages ? '0px 153px 0px 0px' : '0px 20px',
      background: isHome ? 'transparent' : 'var(--header-bg)',
      ...style,
    }}
    {...rest}
  >
    <style>{`
      @media (max-width: 1200px) {
        [data-header-wrapper] { padding: ${isMainPages ? '0px 80px 0px 0px' : '0px'} !important; }
      }
      @media (max-width: 1024px) {
        [data-header-wrapper] { padding: ${isMainPages ? '0px 50px 0px 0px' : '0px'} !important; }
      }
      @media (max-width: 768px) {
        [data-header-wrapper] { padding: ${isMainPages ? '0px 20px 0px 0px' : '0px'} !important; }
      }
    `}</style>
    {children}
  </header>
)

interface DivProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>
}

export const LeftSection = ({ ref, className, children, ...rest }: DivProps) => (
  <div
    ref={ref}
    className={cx('flex items-center justify-start', className)}
    {...rest}
  >
    {children}
  </div>
)

export const RightSection = ({ ref, className, children, ...rest }: DivProps) => (
  <div
    ref={ref}
    className={cx(
      'flex items-center gap-4 w-full font-roboto text-xs font-normal text-(--quaternary-font-color)',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

interface AvatarWrapperProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>
}

const AvatarWrapperBase = ({ ref, className, style, children, ...rest }: AvatarWrapperProps) => (
  <div
    ref={ref}
    className={cx(
      'w-[38px] h-[38px] rounded-full object-cover flex items-center justify-center',
      'border border-[#c4c4c4] p-px text-[17px] font-bold',
      'hover:bg-(--primary-btn-bg) hover:text-(--primary-btn-text)',
      '[&_img]:w-9 [&_img]:h-9 [&_img]:object-cover [&_img]:rounded-full',
      'bg-transparent text-(--main-title-color)',
      className
    )}
    style={style}
    {...rest}
  >
    {children}
  </div>
)

const AvatarInner = ({ ref, className, children, ...rest }: DivProps & { theme?: unknown }) => (
  <div
    ref={ref}
    className={cx(
      'flex items-center justify-center text-[17px] font-bold',
      'hover:bg-(--primary-btn-bg) hover:text-(--primary-btn-text)',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

export const AvatarWrapper = Object.assign(AvatarWrapperBase, {
  Avatar: AvatarInner,
})

export const RightMenu = ({ ref, className, children, ...rest }: DivProps) => (
  <div
    ref={ref}
    className={cx('flex items-center justify-between w-full', className)}
    {...rest}
  >
    {children}
  </div>
)

const RightSubMenuWrapperBase = ({ ref, className, children, ...rest }: DivProps) => (
  <div
    ref={ref}
    className={cx('flex items-center gap-4 ml-auto w-max', className)}
    {...rest}
  >
    {children}
  </div>
)

const RightSubMenuRight = ({ ref, className, children, ...rest }: DivProps) => (
  <div
    ref={ref}
    className={cx('flex items-center gap-4', className)}
    {...rest}
  >
    {children}
  </div>
)

const RightSubMenuLeft = ({ ref, className, children, ...rest }: DivProps) => (
  <div
    ref={ref}
    className={cx('flex items-center gap-4 min-w-[200px] w-max', className)}
    {...rest}
  >
    {children}
  </div>
)

export const RightSubMenuWrapper = Object.assign(RightSubMenuWrapperBase, {
  Right: RightSubMenuRight,
  Left: RightSubMenuLeft,
})
